import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { enrollmentService } from '@/services/enrollmentService'
import type { EnrollmentFilters } from '@/types/enrollment'

export function useEnrollment(id: string) {
  return useQuery({
    queryKey: ['enrollment', id],
    queryFn: () => enrollmentService.getEnrollment(id),
    enabled: !!id,
  })
}

export function useEnrollments(filters: EnrollmentFilters = {}) {
  return useQuery({
    queryKey: ['enrollments', filters],
    queryFn: async () => {
      const response = await enrollmentService.getUserEnrollments(filters.userId || '', filters)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch enrollments')
      }
      return response.data
    },
    enabled: !!filters.userId,
  })
}

export function useUserEnrollments(userId: string, filters: EnrollmentFilters = {}) {
  return useQuery({
    queryKey: ['enrollments', userId, filters],
    queryFn: () => enrollmentService.getUserEnrollments(userId, filters),
    enabled: !!userId,
  })
}

export function useEnrollmentsByCourse(courseId: string) {
  return useQuery({
    queryKey: ['enrollments', 'course', courseId],
    queryFn: async () => {
      const response = await enrollmentService.getByCourse(courseId)
      // Transform EnrollmentWithUser response to Enrollment format
      // Backend returns: enrollmentId, userId, name, email, gender, status, enrolledAt
      // Frontend expects: id, userId, userName, userEmail, status
      if (response.success && Array.isArray(response.data)) {
        const transformed = response.data.map((item: any) => ({
          id: item.enrollmentId || item.id,
          userId: item.userId,
          courseId: '',
          status: item.status,
          paymentStatus: 'paid',
          amountPaid: 0,
          originalPrice: 0,
          progress: 0,
          startedAt: item.enrolledAt || '',
          createdAt: item.enrolledAt || '',
          updatedAt: '',
          userName: item.name || item.userName,
          userEmail: item.email || item.userEmail,
        }))
        return { ...response, data: transformed }
      }
      return response
    },
    enabled: !!courseId,
  })
}

export function useEnrollInCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ courseId, batchId }: { courseId: string; batchId?: string }) =>
      enrollmentService.enrollInCourse(courseId, batchId),
    onSuccess: (response) => {
      if (response.data) {
        // Invalidate all enrollments queries to ensure the new enrollment is picked up
        queryClient.invalidateQueries({ queryKey: ['enrollments'] })
        queryClient.invalidateQueries({ queryKey: ['courses', response.data.courseId] })
      }
    },
  })
}

export function useCancelEnrollment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (enrollmentId: string) => enrollmentService.cancelEnrollment(enrollmentId),
    onSuccess: (response, enrollmentId) => {
      queryClient.invalidateQueries({ queryKey: ['enrollment', enrollmentId] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['enrollments', response.data.userId] })
      }
    },
  })
}
